'use client';

/**
 * Right-edge "Recent" panel tabs:
 *   - ChatsTab        → most-recent chat threads (focus-or-add agent node)
 *   - RunsTab         → /api/users/me/invocations (paged by startedAt)
 *   - TransactionsTab → /api/balance/transactions (paged by offset)
 *
 * Each tab owns its own data + pagination state. We deliberately don't
 * share a generic data-source hook — the three sources page differently
 * (Chats is fully client-side over a single GET, Runs uses cursor,
 * Transactions uses offset) and trying to unify them just produces an
 * abstraction the next reader has to learn.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Zap,
  Hourglass,
  CheckCircle2,
  XCircle,
  Clock,
  AlertCircle,
  ArrowDownLeft,
  ArrowUpRight,
  Wallet,
} from 'lucide-react';
import { useReactFlow } from '@xyflow/react';
import { useT } from '@/components/i18n-provider';
import { getPlacement } from '@/lib/workspaces/types';
import { Avatar } from '@/components/avatar';
import { useAuth } from '@/components/auth-provider';
import { Spinner } from '@/components/ui/spinner';
import { cn, formatRelativeTime } from '@/lib/utils';
import { formatAmount, formatCredits } from '@/lib/balance/units';
import { PanelSearchInput } from './panel-ui';
import {
  useWorkspaceActions,
  useWorkspaceItems,
} from '@/components/workspace/store';
import { chatParticipants } from '@/lib/chat/participants';
import { useInfiniteScroll } from './library-tabs';

const PAGE_LIMIT = 30;

// ===========================================================================
// Shared search input
// ===========================================================================

function PanelSearch({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
}) {
  return (
    <div className="px-2 pt-1.5 pb-1">
      {/* Shared panel search input — identical field across every view
          (autofocus + clear affordance live inside it). */}
      <PanelSearchInput value={value} onChange={onChange} placeholder={placeholder} />
    </div>
  );
}

// ===========================================================================
// ChatsTab
// ===========================================================================

type ChatPreviewParticipant = {
  id: string;
  username: string;
  displayName?: string;
  avatarUrl?: string;
  accountType: 'human' | 'agent';
  /** Only present for delegate sub-chats fetched with
   *  `includeSubchats=1` — disambiguates caller (`owner`) vs callee
   *  (`member`). */
  role?: 'owner' | 'member';
};

type ChatPreview = {
  id: string;
  /** Includes 'delegate*' variants when `includeSubchats=1` was passed. */
  type: 'dm' | 'group' | 'delegate' | 'delegate-ephemeral' | 'delegate-manual';
  /** Auto-parented `delegate` sub-chats carry their parent chat id. */
  parentChatId?: string;
  name?: string;
  avatarUrl?: string;
  lastMessage?: string;
  lastMessageAt?: string;
  unreadCount?: number;
  participants: ChatPreviewParticipant[];
};

const SUBCHAT_TYPES = new Set([
  'delegate',
  'delegate-ephemeral',
  'delegate-manual',
]);

const isSubchat = (c: ChatPreview): boolean => SUBCHAT_TYPES.has(c.type);

/**
 * /api/messages returns all of a user's chats in one shot (no server
 * pagination). For the Recent panel we still want a graceful infinite
 * scroll — long chat lists would otherwise dump hundreds of rows at
 * mount. We page the full list client-side via `visible`, growing it
 * by PAGE_LIMIT each time the sentinel intersects.
 *
 * `?includeSubchats=1` opts us into A2A delegate sub-chats — the
 * workspace is the *one* surface where we want users to see what
 * their agents are saying to each other; the legacy /chats inbox
 * still hides them so the human-only conversation list stays focused.
 */
export function ChatsTab() {
  const t = useT();
  const [chats, setChats] = useState<ChatPreview[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [visible, setVisible] = useState(PAGE_LIMIT);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const c = new AbortController();
    fetch('/api/messages?includeSubchats=1', { signal: c.signal })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (c.signal.aborted || !data) return;
        const list: ChatPreview[] = data.chats ?? [];
        list.sort((a, b) => {
          const ta = a.lastMessageAt ? Date.parse(a.lastMessageAt) : 0;
          const tb = b.lastMessageAt ? Date.parse(b.lastMessageAt) : 0;
          return tb - ta;
        });
        setChats(list);
      })
      .catch(() => {})
      .finally(() => {
        if (!c.signal.aborted) setLoading(false);
      });
    return () => c.abort();
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return chats;
    return chats.filter((c) => {
      if (c.name?.toLowerCase().includes(q)) return true;
      if (c.lastMessage?.toLowerCase().includes(q)) return true;
      return c.participants.some(
        (p) =>
          p.username.toLowerCase().includes(q) ||
          p.displayName?.toLowerCase().includes(q),
      );
    });
  }, [chats, search]);

  // Reset window on filter change so the user always sees top matches.
  useEffect(() => setVisible(PAGE_LIMIT), [search]);

  const hasMore = visible < filtered.length;
  const loadMore = useCallback(() => {
    setVisible((v) => Math.min(v + PAGE_LIMIT, filtered.length));
  }, [filtered.length]);
  useInfiniteScroll(scrollRef, sentinelRef, hasMore, loadMore);

  return (
    <div className="flex flex-col h-full">
      <PanelSearch value={search} onChange={setSearch} placeholder={t('workspace.panels.searchChats')} />
      <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto">
        {loading && (
          <div className="px-3 py-4 text-xs text-muted-foreground">Loading…</div>
        )}
        {!loading && filtered.length === 0 && (
          <div className="px-3 py-6 text-xs text-muted-foreground select-none">
            {search ? t('workspace.panels.noMatchingChats') : t('workspace.panels.noChatsYet')}
          </div>
        )}
        {!loading && filtered.length > 0 && (
          <ul className="px-1 py-1 space-y-0.5">
            {filtered.slice(0, visible).map((chat) => (
              <ChatRow key={chat.id} chat={chat} />
            ))}
          </ul>
        )}
        {hasMore && <div ref={sentinelRef} aria-hidden className="h-4" />}
      </div>
    </div>
  );
}

// =============================================================================
// Stacked avatar group — mirrors the old /chats sidebar's group-chat avatar.
// Kept inline in this file (rather than a shared component) because the
// sizing/positioning is tuned for the tighter density of the workspace
// recent panel and isn't reused elsewhere yet.
// =============================================================================
// Loosely typed so we can feed it the canonical participant list
// returned by `chatParticipants`, which marks fields optional to be
// resilient across the slightly different shapes of /api/messages
// and /api/me/chats/children.
type StackedAvatarParticipant = {
  id?: string;
  username?: string;
  displayName?: string;
  avatarUrl?: string;
  accountType?: 'human' | 'agent';
};

function StackedAvatars({
  participants,
  fallbackSeed,
}: {
  participants: readonly StackedAvatarParticipant[];
  fallbackSeed: string;
}) {
  if (participants.length === 0) {
    return (
      <Avatar
        seed={fallbackSeed}
        type="human"
        className="h-7 w-7 shrink-0"
      />
    );
  }
  if (participants.length === 1) {
    const p = participants[0];
    return (
      <Avatar
        seed={p.username ?? p.id ?? fallbackSeed}
        type={p.accountType ?? 'human'}
        src={p.avatarUrl}
        className="h-7 w-7 shrink-0"
      />
    );
  }
  // 2+ participants: render two overlapping avatars (top-left, bottom-right)
  // inside the same 28px box so the row height stays identical to single-
  // avatar rows. Excess participants collapse into a "+N" badge in the
  // bottom-right corner — matches the old /chats sidebar pattern.
  const visible = participants.slice(0, 2);
  const extra = participants.length - visible.length;
  return (
    <div className="relative h-7 w-7 shrink-0">
      {visible.map((p, idx) => (
        <Avatar
          key={p.id ?? `${p.username ?? fallbackSeed}-${idx}`}
          seed={p.username ?? p.id ?? fallbackSeed}
          type={p.accountType ?? 'human'}
          src={p.avatarUrl}
          className={cn(
            'absolute h-[18px] w-[18px] border-2 border-background',
            idx === 0 ? 'top-0 left-0' : 'bottom-0 right-0',
          )}
        />
      ))}
      {extra > 0 && (
        <div className="absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5 rounded-full bg-muted border border-background flex items-center justify-center">
          <span className="text-[8px] font-semibold leading-none text-muted-foreground">
            +{extra}
          </span>
        </div>
      )}
    </div>
  );
}

function ChatRow({ chat }: { chat: ChatPreview }) {
  const t = useT();
  const items = useWorkspaceItems();
  const { addItem, patchItemState } = useWorkspaceActions();
  const { user: currentUser } = useAuth();
  const flow = useReactFlow();

  // For delegate sub-chats: prefer the CALLEE (role='member') as the
  // spawn target so opening the row lands on the agent that was
  // actually delegated to — that's the conversation the user wants to
  // observe. For non-sub-chats the previous heuristic (first agent
  // participant) stands.
  const subchat = isSubchat(chat);
  const callee = subchat
    ? chat.participants.find(
        (p) => p.role === 'member' && p.accountType === 'agent',
      )
    : undefined;
  const target =
    callee
    ?? chat.participants.find((p) => p.accountType === 'agent')
    ?? chat.participants[0];

  // Display name for the row title:
  //   - explicit chat.name wins (rename)
  //   - sub-chats with two named agents: "@caller → @callee"
  //   - DM: the other participant's display name / username
  //   - fallback: "Chat"
  let display: string;
  if (chat.name) {
    display = chat.name;
  } else if (subchat) {
    const caller = chat.participants.find(
      (p) => p.role === 'owner' && p.accountType === 'agent',
    );
    const calleeName = callee?.displayName || callee?.username;
    const callerName = caller?.displayName || caller?.username;
    if (callerName && calleeName) {
      display = `@${callerName} → @${calleeName}`;
    } else if (calleeName) {
      display = `@${calleeName}`;
    } else {
      display = chat.participants
        .filter((p) => p.accountType === 'agent')
        .map((p) => `@${p.username}`)
        .join(' → ') || 'Sub-chat';
    }
  } else {
    display = target?.displayName || target?.username || 'Chat';
  }

  const avatarSrc = chat.avatarUrl || target?.avatarUrl || null;

  const focusOrAdd = async () => {
    if (!target) return;
    const existing = items.find(
      (it) => it.type === 'agent' && it.state.chatId === chat.id,
    );
    if (existing) {
      if (getPlacement(existing.state) === 'docked') {
        await patchItemState(existing.id, { placement: 'canvas' });
      }
      flow.setCenter(
        existing.state.x + existing.state.width / 2,
        existing.state.y + existing.state.height / 2,
        { zoom: flow.getZoom(), duration: 280 },
      );
      return;
    }
    await addItem({
      type: 'agent',
      refId: target.id,
      state: {
        chatId: chat.id,
        cache: {
          username: target.username,
          displayName: target.displayName ?? target.username,
          avatarUrl: target.avatarUrl ?? avatarSrc,
        },
      },
    }).catch(() => {});
  };

  return (
    <li>
      <button
        type="button"
        onClick={focusOrAdd}
        className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-foreground/6 focus:bg-foreground/6 focus:outline-none text-left transition-colors"
      >
        <StackedAvatars
          participants={chatParticipants(chat.participants, currentUser)}
          fallbackSeed={chat.id}
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="text-xs font-medium truncate">{display}</span>
            {subchat && (
              <span
                title={t('workspace.panels.agentSubChat')}
                className="shrink-0 text-[9px] font-medium uppercase tracking-wider px-1 py-px rounded bg-foreground/8 text-muted-foreground"
              >
                {t('workspace.panels.agentSubChatBadge')}
              </span>
            )}
            {chat.lastMessageAt && (
              <span className="ml-auto text-[10px] text-muted-foreground tabular-nums shrink-0">
                {formatRelativeTime(chat.lastMessageAt)}
              </span>
            )}
          </div>
          {chat.lastMessage && (
            <div className="text-[10px] text-muted-foreground truncate">
              {chat.lastMessage}
            </div>
          )}
        </div>
        {typeof chat.unreadCount === 'number' && chat.unreadCount > 0 ? (
          <span
            className={cn(
              'shrink-0 text-[10px] px-1.5 py-0.5 rounded-full font-semibold',
              'bg-primary/15 text-primary',
            )}
          >
            {chat.unreadCount}
          </span>
        ) : null}
      </button>
    </li>
  );
}

// ===========================================================================
// RunsTab
// ===========================================================================

type RunRow = {
  id: string;
  agentId: string;
  agentUsername: string | null;
  agentAvatarUrl: string | null;
  functionName: string;
  sourceSkill: string;
  status: string; // ok | error | timeout | quota_exceeded
  durationMs: number;
  errorCode: string | null;
  errorMessage: string | null;
  startedAt: string;
};

type HoldRow = {
  agentId: string | null;
  agentUsername: string | null;
  agentDisplayName: string | null;
  agentAvatarUrl: string | null;
  heldCredits: number;
  since: string;
};

/**
 * Work in progress: credits an agent has reserved for a run that has not
 * settled yet. This is the consumer-facing half of the retired Payment
 * Tokens inspector — grouped by AGENT, no token ids, no lock plumbing.
 *
 * Renders nothing when there are no holds, which is the normal state: holds
 * appear for seconds during a run and are released on settle (or by the
 * hourly stale-lock sweep if a run dies mid-flight).
 */
function ActiveHolds() {
  const t = useT();
  const [holds, setHolds] = useState<HoldRow[]>([]);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    const c = new AbortController();
    const load = () => {
      fetch('/api/users/me/holds', { signal: c.signal })
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => {
          if (c.signal.aborted || !d) return;
          setHolds(d.items ?? []);
          // `totalCredits` is what /api/users/me/holds answers as of
          // 2026-09-20 (beside the legacy `totalDollars`). The `?? 0` is
          // why this line mattered: on the wrong name it would have shown
          // a silent zero, not an error.
          setTotal(d.totalCredits ?? 0);
        })
        .catch(() => undefined);
    };
    load();
    // Holds are short-lived; a slow poll keeps the section honest without
    // making this a live socket.
    const id = setInterval(load, 20_000);
    return () => {
      clearInterval(id);
      c.abort();
    };
  }, []);

  if (holds.length === 0) return null;

  return (
    <div className="px-3 pt-3 pb-2 border-b border-border/50">
      <div className="flex items-center gap-1.5 mb-2">
        <Hourglass className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
        <span className="text-xs font-medium">{t('workspace.panels.workInProgress')}</span>
        <span className="ml-auto text-xs tabular-nums text-muted-foreground">
          {formatAmount(total)} held
        </span>
      </div>
      <ul className="space-y-1">
        {holds.map((h) => (
          <li key={h.agentId ?? h.agentUsername ?? String(h.since)} className="flex items-center gap-2">
            <Avatar
              seed={h.agentUsername ?? 'agent'}
              type="agent"
              src={h.agentAvatarUrl ?? undefined}
              className="h-4 w-4 shrink-0"
            />
            <span className="text-[11px] truncate">
              {h.agentUsername ? `@${h.agentUsername}` : (h.agentDisplayName ?? 'Agent')}
            </span>
            <span className="ml-auto text-[11px] tabular-nums text-muted-foreground shrink-0">
              {formatAmount(h.heldCredits)}
            </span>
            <span className="text-[10px] text-muted-foreground shrink-0 w-12 text-right">
              {formatRelativeTime(h.since)}
            </span>
          </li>
        ))}
      </ul>
      <p className="text-[10px] text-muted-foreground mt-2">
        {t('workspace.panels.reservedWhileRunning')}
      </p>
    </div>
  );
}

export function RunsTab() {
  const t = useT();
  const [rows, setRows] = useState<RunRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const buildUrl = useCallback(
    (cursor: string | null) => {
      const params = new URLSearchParams();
      params.set('limit', String(PAGE_LIMIT));
      if (cursor) params.set('cursor', cursor);
      if (search.trim()) params.set('q', search.trim());
      return `/api/users/me/invocations?${params.toString()}`;
    },
    [search],
  );

  // Re-fetch from scratch when the search query changes. Debounced to
  // avoid hammering the API on each keystroke.
  useEffect(() => {
    const c = new AbortController();
    const t = setTimeout(() => {
      setLoading(true);
      setRows([]);
      setHasMore(true);
      setNextCursor(null);
      fetch(buildUrl(null), { signal: c.signal })
        .then((r) => (r.ok ? r.json() : Promise.reject(r.statusText)))
        .then((data) => {
          if (c.signal.aborted) return;
          setRows(data?.items ?? []);
          setHasMore(!!data?.hasMore);
          setNextCursor(data?.nextCursor ?? null);
        })
        .catch((err: Error) => {
          if (err.name === 'AbortError') return;
          setHasMore(false);
        })
        .finally(() => {
          if (!c.signal.aborted) setLoading(false);
        });
    }, search ? 220 : 0);
    return () => {
      clearTimeout(t);
      c.abort();
    };
  }, [buildUrl, search]);

  const loadMore = useCallback(async () => {
    if (loading || loadingMore || !hasMore || !nextCursor) return;
    setLoadingMore(true);
    try {
      const r = await fetch(buildUrl(nextCursor));
      const data = await r.json();
      setRows((prev) => [...prev, ...(data?.items ?? [])]);
      setHasMore(!!data?.hasMore);
      setNextCursor(data?.nextCursor ?? null);
    } catch {
      setHasMore(false);
    } finally {
      setLoadingMore(false);
    }
  }, [buildUrl, loading, loadingMore, hasMore, nextCursor]);
  useInfiniteScroll(scrollRef, sentinelRef, hasMore, loadMore);

  return (
    <div className="flex flex-col h-full">
      <PanelSearch value={search} onChange={setSearch} placeholder={t('workspace.panels.searchRuns')} />
      <ActiveHolds />
      <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto">
        {loading && (
          <div className="px-3 py-4 text-xs text-muted-foreground">Loading…</div>
        )}
        {!loading && rows.length === 0 && (
          <div className="px-3 py-6 text-xs text-muted-foreground select-none">
            {search ? t('workspace.panels.noMatchingRuns') : t('workspace.panels.noRunsYet')}
          </div>
        )}
        {!loading && rows.length > 0 && (
          <ul className="px-1 py-1 space-y-0.5">
            {rows.map((row) => (
              <RunRow key={row.id} row={row} />
            ))}
          </ul>
        )}
        {hasMore && <div ref={sentinelRef} aria-hidden className="h-4" />}
        {loadingMore && (
          <div className="flex justify-center py-2">
            <Spinner size="sm" tone="muted" />
          </div>
        )}
      </div>
    </div>
  );
}

function RunRow({ row }: { row: RunRow }) {
  const tone =
    row.status === 'ok'
      ? 'text-emerald-600 dark:text-emerald-400'
      : row.status === 'timeout'
        ? 'text-amber-600 dark:text-amber-400'
        : 'text-rose-600 dark:text-rose-400';
  const Icon =
    row.status === 'ok'
      ? CheckCircle2
      : row.status === 'timeout'
        ? Clock
        : row.status === 'quota_exceeded'
          ? AlertCircle
          : XCircle;
  return (
    <li>
      <a
        href={
          row.agentUsername
            ? `/@${row.agentUsername}/functions/${row.functionName}`
            : '#'
        }
        target="_blank"
        rel="noopener noreferrer"
        className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-foreground/6 focus:bg-foreground/6 focus:outline-none text-left transition-colors"
      >
        <div className={cn('shrink-0', tone)}>
          <Icon className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="text-xs font-medium truncate">
              {row.functionName}
            </span>
            <span className="ml-auto text-[10px] text-muted-foreground tabular-nums shrink-0">
              {formatRelativeTime(row.startedAt)}
            </span>
          </div>
          <div className="text-[10px] text-muted-foreground truncate flex items-center gap-1.5">
            {row.agentUsername && (
              <span className="truncate">@{row.agentUsername}</span>
            )}
            <span className="inline-flex items-center gap-0.5 tabular-nums">
              <Zap className="h-2.5 w-2.5" />
              {formatDuration(row.durationMs)}
            </span>
            {row.status !== 'ok' && row.errorCode && (
              <span className="truncate uppercase tracking-wider opacity-80">
                {row.errorCode}
              </span>
            )}
          </div>
        </div>
      </a>
    </li>
  );
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.round(ms / 60_000)}m`;
}

// ===========================================================================
// TransactionsTab
// ===========================================================================

type TxRow = {
  id: string;
  amount: string;
  amountCredits: number;
  type: string;
  description: string | null;
  createdAt: string;
};

export function TransactionsTab() {
  const t = useT();
  const [rows, setRows] = useState<TxRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);
  const [search, setSearch] = useState('');
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const c = new AbortController();
    setLoading(true);
    setRows([]);
    setOffset(0);
    setHasMore(true);
    fetch(`/api/balance/transactions?limit=${PAGE_LIMIT}&offset=0`, { signal: c.signal })
      .then((r) => (r.ok ? r.json() : Promise.reject(r.statusText)))
      .then((data) => {
        if (c.signal.aborted) return;
        const list: TxRow[] = data?.transactions ?? [];
        setRows(list);
        if (list.length < PAGE_LIMIT) setHasMore(false);
      })
      .catch((err: Error) => {
        if (err.name === 'AbortError') return;
        setHasMore(false);
      })
      .finally(() => {
        if (!c.signal.aborted) setLoading(false);
      });
    return () => c.abort();
  }, []);

  const loadMore = useCallback(async () => {
    if (loading || loadingMore || !hasMore) return;
    setLoadingMore(true);
    const next = offset + PAGE_LIMIT;
    try {
      const r = await fetch(
        `/api/balance/transactions?limit=${PAGE_LIMIT}&offset=${next}`,
      );
      const data = await r.json();
      const list: TxRow[] = data?.transactions ?? [];
      setRows((prev) => [...prev, ...list]);
      setOffset(next);
      if (list.length < PAGE_LIMIT) setHasMore(false);
    } catch {
      setHasMore(false);
    } finally {
      setLoadingMore(false);
    }
  }, [loading, loadingMore, hasMore, offset]);
  useInfiniteScroll(scrollRef, sentinelRef, hasMore, loadMore);

  // Transactions API doesn't accept a search query — narrow locally.
  // For thousands of rows this would become a tax, but the dataset
  // here is bounded by the partition window so it's a non-issue.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.type.toLowerCase().includes(q) ||
        (r.description ?? '').toLowerCase().includes(q),
    );
  }, [rows, search]);

  return (
    <div className="flex flex-col h-full">
      <PanelSearch value={search} onChange={setSearch} placeholder={t('workspace.panels.searchTransactions')} />
      <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto">
        {loading && (
          <div className="px-3 py-4 text-xs text-muted-foreground">Loading…</div>
        )}
        {!loading && filtered.length === 0 && (
          <div className="px-3 py-6 text-xs text-muted-foreground select-none">
            {search ? t('workspace.panels.noMatchingTransactions') : t('workspace.panels.noTransactionsYet')}
          </div>
        )}
        {!loading && filtered.length > 0 && (
          <ul className="px-1 py-1 space-y-0.5">
            {filtered.map((row) => (
              <TxRowItem key={row.id} row={row} />
            ))}
          </ul>
        )}
        {hasMore && <div ref={sentinelRef} aria-hidden className="h-4" />}
        {loadingMore && (
          <div className="flex justify-center py-2">
            <Spinner size="sm" tone="muted" />
          </div>
        )}
      </div>
    </div>
  );
}

function TxRowItem({ row }: { row: TxRow }) {
  const credit = row.amountCredits >= 0;
  const Icon = credit ? ArrowDownLeft : ArrowUpRight;
  return (
    <li>
      <div className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-foreground/6 transition-colors">
        <div
          className={cn(
            'h-7 w-7 shrink-0 rounded-full flex items-center justify-center',
            credit
              ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400'
              : 'bg-rose-500/15 text-rose-600 dark:text-rose-400',
          )}
        >
          <Icon className="h-3.5 w-3.5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="text-xs font-medium truncate">
              {prettyTxType(row.type)}
            </span>
            <span
              className={cn(
                'ml-auto text-xs font-semibold tabular-nums shrink-0',
                credit
                  ? 'text-emerald-600 dark:text-emerald-400'
                  : 'text-foreground',
              )}
            >
              {credit ? '+' : ''}
              {formatCredits(row.amountCredits)}
            </span>
          </div>
          <div className="text-[10px] text-muted-foreground truncate flex items-center gap-1.5">
            {row.description && <span className="truncate">{row.description}</span>}
            <span className="ml-auto tabular-nums shrink-0">
              {formatRelativeTime(row.createdAt)}
            </span>
          </div>
        </div>
      </div>
    </li>
  );
}

function prettyTxType(t: string): string {
  switch (t) {
    case 'deposit':
      return 'Deposit';
    case 'withdrawal':
      return 'Withdrawal';
    case 'transfer':
      return 'Transfer';
    case 'referral':
      return 'Referral';
    case 'subscription':
      return 'Subscription';
    case 'token_topup':
      return 'Top-up';
    case 'token_lock':
      return 'Lock';
    case 'token_unlock':
      return 'Unlock';
    case 'token_expired':
      return 'Expired';
    case 'charge_received':
      return 'Charge in';
    case 'a2a_earning':
      return 'Service earning';
    case 'creator_grant':
      return 'Creator Fund grant';
    case 'earnings_adjustment':
      return 'Adjustment';
    case 'charge_sent':
      return 'Charge out';
    case 'byok_llm':
      return 'BYOK LLM';
    case 'byok_mcp_sampling':
      return 'BYOK MCP';
    default:
      return t.replace(/_/g, ' ');
  }
}

// The activity rows read `amountCredits` and, until 2026-09-22, printed it
// through a local `$` formatter; `formatCredits` (lib/balance/units.ts) is the
// one formatter for a credit amount shown to a person.

// Wallet/Icon exported for parent panel header decoration.
export { Wallet };
